import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Item, Bid } from "../types/Item";
import auctionService from "../services/auctionService";
import { useAuth } from "../contexts/AuthContext";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
  IconButton,
} from "@mui/material";
import { GISProperty } from "../types/gis";
import gisService from "../services/gisService";
import GisMap from "./GisMap";
import { formatPrice } from "../utils/formatters";
import { getImageUrl } from "../utils/image";

const ItemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState<Item | null>(null);
  const [bids, setBids] = useState<Bid[]>([]);
  const [gisProperty, setGisProperty] = useState<GISProperty | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bidAmount, setBidAmount] = useState<number>(0);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [mapFullscreen, setMapFullscreen] = useState(false);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && mapFullscreen) {
        setMapFullscreen(false);
      }
    };

    if (mapFullscreen) {
      document.addEventListener("keydown", handleEscKey);
      // Prevent body scroll when fullscreen
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "unset";
    };
  }, [mapFullscreen]);

  useEffect(() => {
    if (id) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const itemData = await auctionService.getLiveAuction(id);
          setItem(itemData);
          if (itemData && itemData.images && itemData.images.length > 0) {
            setSelectedImage(itemData.images[0]);
          }
          if (itemData?.bids) {
            setBids(itemData.bids);
          }
          if (itemData?.parcel_id) {
            try {
              const gisData = await gisService.getGisProperty(
                itemData.parcel_id
              );
              setGisProperty(gisData);
            } catch (gisError) {
              console.error("Failed to fetch GIS data", gisError);
              // Not setting a page-level error for this,
              // as the main item data is more important
            }
          }
        } catch (error) {
          console.error("Failed to fetch item", error);
          setError("Failed to load item details.");
        } finally {
          setLoading(false);
        }
      };
      fetchItem();
    }
  }, [id]);

  const handlePlaceBid = async () => {
    if (!id || bidAmount <= 0) return;
    if (!user) {
      navigate("/login");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await auctionService.placeBid(id, bidAmount);
      const itemData = await auctionService.getLiveAuction(id);
      setItem(itemData);
      setBids(itemData.bids || []);
      setBidAmount(0);
    } catch (err: any) {
      setError(err.message || "Failed to place bid");
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    if (item?.seller_id) {
      const whatsappUrl = `https://wa.me/${item.seller_id}`;
      window.open(whatsappUrl, "_blank");
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!item) return <Typography>Item not found.</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back to Items
      </Button>
      <Card>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CardMedia
              component="img"
              image={getImageUrl(selectedImage)}
              alt={item.title}
              sx={{
                height: 400,
                objectFit: "contain",
                mb: 1,
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "/default-auction.png";
              }}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {item.images?.map((img, index) => (
                <CardMedia
                  key={index}
                  component="img"
                  image={getImageUrl(img)}
                  alt={`${item.title} thumbnail ${index + 1}`}
                  onClick={() => setSelectedImage(img)}
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    cursor: "pointer",
                    border: selectedImage === img ? "2px solid" : "none",
                    borderColor: "primary.main",
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/default-auction.png";
                  }}
                />
              ))}
              {item.video && (
                <Button
                  variant="outlined"
                  onClick={() => window.open(item.video, "_blank")}
                >
                  Play Video
                </Button>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <CardContent>
              <Typography variant="h4" gutterBottom>
                {item.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {item.description}
              </Typography>
              <Typography variant="h6">
                Current Price:{" "}
                {formatPrice(item.current_price || item.starting_price)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                (Minimum Increment: {formatPrice(item.min_increment)})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Auction ends:{" "}
                {item.end_time
                  ? new Date(item.end_time).toLocaleString()
                  : "N/A"}
              </Typography>
              {user && (
                <Box sx={{ mt: 3, display: "flex", alignItems: "center" }}>
                  <TextField
                    label="Your Bid"
                    type="number"
                    value={bidAmount}
                    onChange={(e) => setBidAmount(Number(e.target.value))}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 2 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePlaceBid}
                    disabled={loading}
                  >
                    Place Bid
                  </Button>
                </Box>
              )}
              <Button
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                onClick={handleWhatsAppClick}
                sx={{ mt: 2 }}
                disabled={!item.seller_id}
              >
                Contact Seller
              </Button>
            </CardContent>
          </Grid>
        </Grid>
      </Card>

      {gisProperty && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h5" gutterBottom>
            Property Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography>
                <strong>Parcel ID:</strong> {gisProperty.parcel_id}
              </Typography>
              {gisProperty.site_address && (
                <Typography>
                  <strong>Address:</strong> {gisProperty.site_address}
                </Typography>
              )}
              {gisProperty.owner_names && (
                <Typography>
                  <strong>Owner:</strong> {gisProperty.owner_names.join(", ")}
                </Typography>
              )}
              {gisProperty.legal_description && (
                <Typography>
                  <strong>Legal Description:</strong>{" "}
                  {gisProperty.legal_description}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {gisProperty.geometry ? (
                <Box
                  onClick={() => setMapFullscreen(true)}
                  sx={{
                    cursor: "pointer",
                    position: "relative",
                    "&:hover": {
                      "&::after": {
                        content: '"Click to view fullscreen"',
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        background: "rgba(0,0,0,0.7)",
                        color: "white",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        zIndex: 1,
                      },
                    },
                  }}
                >
                  <GisMap
                    properties={[gisProperty]}
                    style={{ height: "300px", width: "100%" }}
                  />
                </Box>
              ) : (
                <Typography>No map data available.</Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Bid History
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {bids.length > 0 ? (
          <List>
            {bids
              .slice()
              .sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime()
              )
              .map((bid) => (
                <ListItem key={bid.id} disablePadding>
                  <ListItemText
                    primary={`$${bid.amount}`}
                    secondary={`by ${bid.user_id} at ${new Date(
                      bid.created_at
                    ).toLocaleString()}`}
                  />
                </ListItem>
              ))}
          </List>
        ) : (
          <Typography>No bids yet.</Typography>
        )}
      </Paper>

      {/* Fullscreen Map Overlay */}
      {mapFullscreen && gisProperty?.geometry && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              color: "white",
            }}
          >
            <Typography variant="h6">
              Property Map - {gisProperty.parcel_id}
            </Typography>
            <IconButton
              onClick={() => setMapFullscreen(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, p: 2 }}>
            <GisMap
              properties={[gisProperty]}
              style={{ height: "100%", width: "100%" }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ItemDetail;
